import React, { useState } from 'react';
import { 
  ImageList, 
  ImageListItem, 
  Box, 
  Typography, 
  Chip,
  Popover,
  IconButton,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  Paper,
  Tooltip,
  Divider,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Folder, 
  Maximize, 
  PlayCircle, 
  Calendar, 
  Film, 
  Tag, 
  Clock,
  Monitor
} from 'lucide-react';

// Styled components for list view
const ListViewItem = styled(Paper)(({ theme, isSelected }) => ({
  display: 'flex',
  width: '100%',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.background.paper, 0.6),
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)'
  }
}));

const ListViewThumbnail = styled(Box)(({ theme }) => ({
  width: '160px',
  height: '90px',
  borderRadius: '6px',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative'
}));

const ListViewDetails = styled(Box)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0 // This is important for flexbox to respect child overflow
}));

const ListViewHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  width: '100%'
}));

const ListViewMetadata = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginTop: 'auto'
}));

const ListViewMetaItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem'
}));

const EnhancedVideoList = ({ 
  items, 
  onVideoClick, 
  onVideoSelect, 
  selectedVideos, 
  isSmallScreen,
  onContextMenu,
  viewMode,
  videoTags = {}
}) => {
  const [expandedTags, setExpandedTags] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format video duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleTagsExpand = (event, tags) => {
    setExpandedTags(tags);
    setAnchorEl(event.currentTarget);
  };

  const handleTagsClose = () => {
    setExpandedTags(null);
    setAnchorEl(null);
  };

  // Calculate the number of columns based on view mode and screen size
  const getColumnCount = () => {
    if (viewMode === 'list') {
      return 1; // Single column for list view
    }
    return isMobile ? 2 : 4; // Default grid view
  };

  // Render list view
  if (viewMode === 'list') {
    return (
      <>
        <List sx={{ width: '100%', padding: 0 }}>
          {items.map((result) => (
            <ListItem 
              key={result.video_url} 
              disablePadding 
              onContextMenu={(e) => onContextMenu(e, result.video_url)}
              sx={{ mb: 1 }}
            >
              <ListViewItem 
                isSelected={selectedVideos.includes(result.video_url)}
                onClick={(e) => {
                  e.stopPropagation();
                  onVideoSelect(result.video_url);
                }}
              >
                <ListViewThumbnail>
                  <img
                    src={result.thumbnail_url || result.video_url}
                    alt={result.class_label || 'Video'}
                    loading="lazy"
                    style={{ 
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  
                  {/* Duration overlay */}
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 1,
                      fontSize: '0.7rem'
                    }}
                  >
                    {formatDuration(result.duration)}
                  </Typography>
                  
                  {/* Play button overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                      '&:hover': {
                        opacity: 1,
                        cursor: 'pointer'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onVideoClick(result.video_url);
                    }}
                  >
                    <PlayCircle size={36} color="#fff" />
                  </Box>
                </ListViewThumbnail>
                
                <ListViewDetails>
                  <ListViewHeader>
                    <Box>
                      <Tooltip title={result.class_label || 'Untitled'}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600,
                            maxWidth: { xs: '240px', sm: '400px', md: '600px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {result.class_label || 'Untitled'}
                        </Typography>
                      </Tooltip>
                      
                      {result.folder_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Folder size={14} strokeWidth={1.5} />
                          <Tooltip title={result.folder_name}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{
                                maxWidth: { xs: '200px', sm: '300px', md: '500px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {result.folder_name}
                            </Typography>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* View/expand button */}
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onVideoClick(result.video_url);
                        }}
                        sx={{
                          color: 'text.secondary',
                          p: '4px',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }
                        }}
                        size="small"
                      >
                        <PlayCircle size={16} />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onVideoClick(result.video_url);
                        }}
                        sx={{
                          color: 'text.secondary',
                          p: '4px',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main
                          }
                        }}
                        size="small"
                      >
                        <Maximize size={16} />
                      </IconButton>
                    </Box>
                  </ListViewHeader>
                  
                  {/* Tags */}
                  {(result.tags && result.tags.length > 0) || (videoTags[result.video_url] && videoTags[result.video_url].length > 0) ? (
                    <Box sx={{ my: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Tag size={14} strokeWidth={1.5} style={{ marginRight: '2px', color: theme.palette.text.secondary }} />
                      {(result.tags || videoTags[result.video_url] || []).slice(0, 4).map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="small"
                          sx={{ 
                            height: '20px',
                            '& .MuiChip-label': {
                              px: 1,
                              fontSize: '0.75rem'
                            },
                            bgcolor: 'grey.100'
                          }}
                        />
                      ))}
                      {(result.tags?.length > 4 || videoTags[result.video_url]?.length > 4) && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ 
                            alignSelf: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagsExpand(e, result.tags || videoTags[result.video_url]);
                          }}
                        >
                          +{(result.tags?.length || videoTags[result.video_url]?.length) - 4}
                        </Typography>
                      )}
                    </Box>
                  ) : null}
                  
                  <ListViewMetadata>
                    {/* Duration */}
                    <ListViewMetaItem>
                      <Clock size={14} strokeWidth={1.5} />
                      <Typography variant="caption">
                        {formatDuration(result.duration)}
                      </Typography>
                    </ListViewMetaItem>
                    
                    {/* Date */}
                    <ListViewMetaItem>
                      <Calendar size={14} strokeWidth={1.5} />
                      <Typography variant="caption">
                        {formatDate(result.timestamp)}
                      </Typography>
                    </ListViewMetaItem>
                    
                    {/* Size */}
                    {result.size && (
                      <ListViewMetaItem>
                        <Film size={14} strokeWidth={1.5} />
                        <Typography variant="caption">
                          {formatFileSize(result.size)}
                        </Typography>
                      </ListViewMetaItem>
                    )}
                    
                    {/* Resolution if available */}
                    {result.resolution && (
                      <ListViewMetaItem>
                        <Monitor size={14} strokeWidth={1.5} />
                        <Typography variant="caption">
                          {Array.isArray(result.resolution) 
                            ? `${result.resolution[0]}×${result.resolution[1]}`
                            : result.resolution}
                        </Typography>
                      </ListViewMetaItem>
                    )}
                  </ListViewMetadata>
                </ListViewDetails>
              </ListViewItem>
            </ListItem>
          ))}
        </List>
        
        {/* Tags Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleTagsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>All Tags</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {expandedTags?.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{ 
                    height: '24px',
                    '& .MuiChip-label': {
                      px: 1.5
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Popover>
      </>
    );
  }

  // Original grid view implementation
  return (
    <>
      <ImageList cols={getColumnCount()} gap={16}>
        {items.map((result) => (
          <ImageListItem 
            key={result.video_url}
            sx={{ 
              '&:hover': {
                '.video-info': {
                  opacity: 1,
                },
                '.video-actions': {
                  opacity: 1
                }
              }
            }}
            onContextMenu={(e) => onContextMenu(e, result.video_url)}
          >
            {/* Video thumbnail container with overlay */}
            <Box 
              sx={{ 
                position: 'relative',
                borderRadius: '8px',
                overflow: 'hidden',
                aspectRatio: '16/9',
                mb: 1,
                bgcolor: 'grey.100',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onVideoSelect(result.video_url);
              }}
            >
              <img
                src={result.thumbnail_url || result.video_url}
                alt={result.class_label || 'Video'}
                loading="lazy"
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />

              {/* Duration overlay in top-right */}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.7rem'
                }}
              >
                {formatDuration(result.duration)}
              </Typography>

              {/* Resolution indicator */}
              {result.resolution && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.7rem'
                  }}
                >
                  {Array.isArray(result.resolution) 
                    ? `${result.resolution[0]}×${result.resolution[1]}`
                    : result.resolution}
                </Typography>
              )}

              {/* Play button icon overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                  '&:hover': {
                    opacity: 1
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onVideoClick(result.video_url);
                }}
              >
                <PlayCircle size={48} color="#fff" />
              </Box>

              {/* View/expand button */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onVideoClick(result.video_url);
                }}
                className="video-actions"
                sx={{
                  position: 'absolute',
                  right: 8,
                  bottom: 8,
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  p: '6px',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 2
                }}
                size="small"
              >
                <Maximize size={18} />
              </IconButton>

              {/* Selection overlay - visual indicator only */}
              {selectedVideos.includes(result.video_url) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(25, 118, 210, 0.2)',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: '7px'
                  }}
                />
              )}
            </Box>

            {/* Video information */}
            <Box className="video-info" sx={{ px: 1 }}>
              {/* Title */}
              <Tooltip title={result.class_label || 'Untitled'}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 500,
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {result.class_label || 'Untitled'}
                </Typography>
              </Tooltip>

              {/* File size if available */}
              {result.size && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  {formatFileSize(result.size)}
                </Typography>
              )}

              {/* Tags */}
              {(result.tags || videoTags[result.video_url]) && (result.tags?.length > 0 || videoTags[result.video_url]?.length > 0) && (
                <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(result.tags || videoTags[result.video_url] || []).slice(0, 3).map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      sx={{ 
                        height: '20px',
                        '& .MuiChip-label': {
                          px: 1,
                          fontSize: '0.75rem'
                        },
                        bgcolor: 'grey.100'
                      }}
                    />
                  ))}
                  {(result.tags?.length > 3 || videoTags[result.video_url]?.length > 3) && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ 
                        alignSelf: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                      onClick={(e) => handleTagsExpand(e, result.tags || videoTags[result.video_url])}
                    >
                      +{(result.tags?.length || videoTags[result.video_url]?.length) - 3}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Folder Info */}
              {result.folder_name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Folder size={16} strokeWidth={1.5} />
                  <Tooltip title={result.folder_name}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{
                        maxWidth: '90%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {result.folder_name}
                    </Typography>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </ImageListItem>
        ))}
      </ImageList>

      {/* Tags Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleTagsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>All Tags</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {expandedTags?.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                sx={{ 
                  height: '24px',
                  '& .MuiChip-label': {
                    px: 1.5
                  }
                }}
              />
            ))}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default EnhancedVideoList;